/**
 * What the inbox says when a call fails (PROD-1849). One place, so the page, the
 * composer and the tests use the same words.
 */
import { InboxApiError } from './inbox-api';

/**
 * The list or thread could not be read. Both 403s are the no-access state:
 * `no-organization` = 403 `{ "error": "organization_required" }` (the token has no orgId);
 * `forbidden` = a plain 403 with no body (no WhatsApp_Inbox_View / WhatsApp_Channel_Manage).
 */
export type InboxLoadFailure = 'no-organization' | 'forbidden' | 'not-found' | 'unavailable';

export const ORGANIZATION_REQUIRED = 'organization_required';

export function classifyLoadError(error: unknown): InboxLoadFailure {
  if (error instanceof InboxApiError) {
    if (error.status === 403) return error.code === ORGANIZATION_REQUIRED ? 'no-organization' : 'forbidden';
    if (error.status === 404) return 'not-found';
  }
  return 'unavailable';
}

/** No retry for a 4xx: the answer will not change. Network errors and 5xx retry once. */
export function shouldRetryInboxRead(failureCount: number, error: unknown): boolean {
  if (error instanceof InboxApiError && error.status < 500) return false;
  return failureCount < 1;
}

export interface SendFailure {
  message: string;
  /** True when sending the same reply again can work (network, 5xx without a contract code). */
  retryable: boolean;
  /**
   * True when the server may have STORED a failed message for this send (contract, Notifications #282:
   * 409 opted_out / patient_communication_off or 503 channel_unavailable raised during the send).
   * The optimistic bubble is dropped and the thread re-read, so the stored message shows once, and the
   * text is not put back in the composer (it is in the thread). Otherwise nothing was stored and the
   * text goes back in the composer.
   */
  storedOnServer: boolean;
}

export const SEND_NOT_AVAILABLE = 'Sending from the inbox is not available yet.';

const failure = (message: string, opts: Partial<Omit<SendFailure, 'message'>> = {}): SendFailure => ({
  message,
  retryable: opts.retryable ?? false,
  storedOnServer: opts.storedOnServer ?? false,
});

/** PROD-3341: a failed template send. The server stores nothing for these (it refuses before the send). */
export function describeTemplateSendError(error: unknown): SendFailure {
  if (error instanceof InboxApiError) {
    const { status, code } = error;
    if (status === 409 && code === 'opted_out') return failure('Not sent. This patient has opted out of WhatsApp messages.');
    if (status === 409 && code === 'patient_communication_off') {
      return failure('Not sent. Patient messages are switched off for this clinic.');
    }
    if (status === 409 && code === 'template_not_ready') {
      return failure('Not sent. This template is not approved for the clinic, or the appointment is missing a detail it needs.');
    }
    if (status === 503 && code === 'channel_unavailable') return failure('Not sent. The clinic has no live WhatsApp number.');
    if (status === 409 && code === 'appointment_unreadable') {
      return failure("Not sent. This conversation's appointment could not be read. Try again later.");
    }
    if (status === 400 && code === 'appointment_not_offered') {
      return failure('Not sent. The appointment changed. Choose the template again.');
    }
    if (status === 404 && code === 'conversation_not_found') return failure('Not sent. This conversation was not found.');
    if (status === 404 || status === 405) return failure('Sending a template from the inbox is not available yet.');
    if (status === 403) return failure('Not sent. You do not have permission to send from the WhatsApp inbox.');
    if (status === 400) return failure('Not sent. Choose the template again.');
  }
  return failure('Not sent. Check the connection and try again.', { retryable: true });
}

/** PROD-3341: the send's request to Meta broke off (a `unknown` Message): it may have reached the patient. */
export const TEMPLATE_OUTCOME_UNKNOWN =
  'WhatsApp may have sent the template. Check the thread for its status before sending it again.';

/** PROD-3341: a template Meta refused (the 202 Message is `failed`; failureReason is Meta's "code: reason"). */
export function describeTemplateRefusal(failureReason: string | null | undefined): string {
  return failureReason ? `WhatsApp did not send the template: ${failureReason}` : 'WhatsApp did not send the template.';
}

/** Maps a failed staff reply (contract endpoint 4) to the composer's inline error. */
export function describeSendError(error: unknown): SendFailure {
  if (error instanceof InboxApiError) {
    const { status, code } = error;
    if (status === 409 && code === 'session_window_closed') {
      return failure('Not sent. The 24-hour reply window is closed, so only an approved template can reach this patient.');
    }
    if (status === 409 && code === 'opted_out') {
      return failure('Not sent. This patient has opted out of WhatsApp messages.', { storedOnServer: true });
    }
    if (status === 409 && code === 'patient_communication_off') {
      return failure('Not sent. Patient messages are switched off for this clinic.', { storedOnServer: true });
    }
    if (status === 503 && code === 'channel_unavailable') {
      return failure('Not sent. The clinic has no live WhatsApp number.', { storedOnServer: true });
    }
    if (status === 404 && code === 'conversation_not_found') {
      return failure('Not sent. This conversation was not found.');
    }
    // Endpoint 4 is not on the gateway yet (Notifications #282): 404 without the contract code, or 405.
    if (status === 404 || status === 405) return failure(SEND_NOT_AVAILABLE);
    if (status === 403 && code === ORGANIZATION_REQUIRED) return failure('Not sent. Your account has no organization.');
    if (status === 403) return failure('Not sent. You do not have permission to reply from the WhatsApp inbox.');
    if (status === 400 && code === 'invalid_client_message_id') {
      return failure('Not sent. The reply could not be identified. Send it again.', { retryable: false });
    }
    if (status === 400) return failure('Not sent. The reply must be 1 to 4096 characters.');
  }
  return failure('Not sent. Check the connection and try again.', { retryable: true });
}

